(ns frontend.view.entity-viewers.modules
  (:require
   [reagent.core :as r]
   [frontend.utils :as utils]
   [frontend.state.current-session :as session]
   [frontend.view.entity-viewers.module-card :as module-card]
   [potpuri.core :as p :refer [fn-> fn->> index-by deep-merge dissoc-in]]
   [frontend.logging :as logging]))

(defn sorted-module-ids
  [modules ordering]
  (let [ordered-ids (set ordering)
        unordered-ids (->> modules
                           (map :module-id)
                           (remove (fn->> (contains? ordered-ids))))]
    (->> (concat ordering unordered-ids)
         (remove nil?))))

(defn sort-by-ids
  [modules module-ids]
  (let [ords (->> module-ids
                  (map-indexed (fn [i m] [m i]))
                  (into {}))]
    (sort-by (fn->> :module-id ords) modules)))

(defn put-before
  [items pos item]
  (let [items (remove #{item} items)
        head (take pos items)
        tail (drop pos items)]
    (vec (concat head [item] tail))))

(defn index-of
  [items item]
  (count (take-while (comp not #{item}) items)))

(defn on-drag-start
  [drag-atom module-ids module-id]
  (swap! drag-atom assoc
    :drag-id module-id
    :ordering module-ids
    :force-collapsed? true))

(defn toggle-collapse
  [view-id module-id collapsed-path collapsed? save-module-collapsed! new-collapsed?]
  ;;optimistic save to frontend db:
  (swap! session/view-config assoc-in collapsed-path new-collapsed?)
  (deref session/view-config)
  (save-module-collapsed!
   view-id
   module-id
   new-collapsed?
   identity
   (fn [_] ;;on-error
     ;;retract the optimistic save above
     (when (= new-collapsed? (get-in @session/view-config collapsed-path))
       (swap! session/view-config assoc-in collapsed-path collapsed?)))))

(defn toggle-expand
  [view-id module-id expanded-path expanded? save-module-expanded! new-expanded?]
  ;;optimistic save to frontend db:
  (swap! session/view-config assoc-in expanded-path new-expanded?)
  (deref session/view-config)
  (save-module-expanded!
   view-id
   module-id
   new-expanded?
   identity
   (fn [_] ;;on-error
     ;;retract the optimistic save above
     (when (= new-expanded? (get-in @session/view-config expanded-path))
       (swap! session/view-config assoc-in expanded-path expanded?)))))

(defn view
  [{:keys [view-id save-modules-order!]}]
  (let [timeout-atom (atom {})
        drag-atom (r/atom {})
        on-drag-over! (fn [pos module-id]
                        (let [{:keys [drag-id ordering]} @drag-atom]
                          (when-not (or (= module-id drag-id)
                                        (nil? drag-id))
                            (swap! drag-atom update :ordering put-before pos drag-id))))
        on-drag-end! (fn [module-id]
                       (logging/info "Clear drag (module-id)" module-id "--> " @drag-atom)
                       (swap! drag-atom dissoc :drag-over :drag-id :force-collapsed?)
                       (utils/temp-timeout
                        {:timeout-state timeout-atom
                         :timeout-path  [:ordering-timeout]
                         :before-save!  identity ;;local state ("temp value") is already set;
                         :on-success!   identity
                         :on-timeout!
                         (fn []
                           (swap! drag-atom dissoc :ordering))
                         :save-fn!
                         (fn [{:keys [on-success!]}]
                           (save-modules-order! view-id
                                                (:ordering @drag-atom)
                                                on-success!
                                                (fn [e]
                                                  (swap! drag-atom dissoc :ordering))))}))
        on-drag-leave! (fn [module-id]
                         (logging/info "Clear drag (module-id)" module-id "--> " @drag-atom)
                         (swap! drag-atom assoc :drag-over :nothing))]
    (fn [{:keys [modules ordering]}]
      (let [modules (remove nil? modules)
            module-ids (sorted-module-ids modules ordering)
            module-ids (or (:ordering @drag-atom) module-ids)
            on-drag-start! (r/partial on-drag-start drag-atom module-ids)]
        [:div
         (doall
          (for [[module pos] (map vector (sort-by-ids modules module-ids) (range))
                :let [module-id (:module-id module)
                      collapsed? (:collapsed? module)
                      collapsed-path [view-id :modules :module-config module-id :collapsed?]
                      expanded? (:expanded? module)
                      expanded-path [view-id :modules :module-config module-id :expanded?]
                      start-collapsed? (:start-collapsed? module)
                      start-expanded? (:start-expanded? module)
                      collapsed? (or (:force-collapsed? @drag-atom) collapsed?)
                      save-module-collapsed! (:save-module-collapsed! module)
                      save-module-expanded! (:save-module-expanded! module)
                      content (:content module)
                      header (:header module)
                      header-tools (:header-tools module)
                      hide-drag-handle? (:hide-drag-handle? module)]]
            ^{:key (str "module-card-" module-id)}
            [module-card/view
             (merge
              (when (or (:force-collapsed? @drag-atom)
                        (not start-collapsed?))
                {:collapsed? collapsed?})
              (when (not start-expanded?)
                {:expanded? expanded?})
              {:drag-atom         drag-atom
               :module-id         module-id
               :class-name        (str module-id)
               :start-collapsed?  start-collapsed?
               :start-expanded?   start-expanded?
               :toggle-collapse!  (r/partial toggle-collapse view-id module-id collapsed-path collapsed? save-module-collapsed!)
               :toggle-expanded!  (when (fn? save-module-expanded!)
                                    (r/partial toggle-expand view-id module-id expanded-path expanded? save-module-expanded!))
               :on-drag-start!    on-drag-start!
               :on-drag-over!     (r/partial on-drag-over! pos)
               :on-drag-leave!    on-drag-leave!
               :on-drag-end!      on-drag-end!
               :header            header
               :header-tools      header-tools
               :hide-drag-handle? hide-drag-handle?})
             (if (fn? content)
               [content module]
               content)]))]))))
